
import { MembershipApplication } from "@/components/admin/membership/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileDown } from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useState } from "react";

interface ApplicationsTableProps {
  applications: MembershipApplication[];
  onViewDetails: (application: MembershipApplication) => void;
  onDownloadPDF: (application: MembershipApplication) => void;
  onStatusChange: (id: string, status: string) => Promise<void>;
}

const ApplicationsTable = ({ 
  applications, 
  onViewDetails, 
  onDownloadPDF,
  onStatusChange
}: ApplicationsTableProps) => {
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    if (updatingId === id) return;
    
    try {
      setUpdatingId(id);
      await onStatusChange(id, newStatus);
    } catch (error) {
      console.error("[ApplicationsTable] Error updating status:", error);
    } finally {
      // Add a slight delay to prevent accidental double-clicks
      setTimeout(() => setUpdatingId(null), 1000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Business</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Current Area</TableHead>
            <TableHead>Total Area</TableHead>
            <TableHead>Planned Area</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {applications?.map((application) => (
            <TableRow 
              key={application.id}
              className="hover:bg-gray-50"
            >
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {new Date(application.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.first_name} {application.last_name}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.business_name}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.membership_type}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.current_potato_area || "-"}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.total_potato_area || "-"}
              </TableCell>
              <TableCell onClick={() => onViewDetails(application)} className="cursor-pointer">
                {application.planned_potato_area || "-"}
              </TableCell>
              <TableCell>
                <Select
                  disabled={updatingId === application.id}
                  value={application.status}
                  onValueChange={(value) => handleStatusChange(application.id, value)}
                >
                  <SelectTrigger className={`w-28 ${getStatusColor(application.status)} text-white border-0`}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-zinc-900 border border-border shadow-md z-50">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadPDF(application);
                  }}
                  title="Download PDF"
                >
                  <FileDown className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ApplicationsTable;
